// 运行中问到缺字段：顶部一眼看清「缺哪个字段、现在的值」；补法分标签页 —— 直接填写、让 AI 助理从选定的已传文件里找（可对话、可说怎么换算）、补一份新文件、给执行端的其它指引。
// 字段表里没有的先登记进任务类型；值确定后写进任务，继续时自动告诉执行端；选的文件记为这个字段的来源。
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { FieldChatMessage, ManifestFileReq, MissingFieldRef, TaskDTO } from "@csp/shared";
import { api, errorText } from "../lib/api";
import { fieldName } from "../lib/fieldName";

type MfTab = "direct" | "ai" | "upload" | "note";

export function MissingFieldHelper({ task, field, why, where, cats, picked, onPick, onChanged, uploadPane, notePane }: {
  task: TaskDTO; field: MissingFieldRef;
  /** 停在哪：「第 N 步 xxx」 */
  where: string | null;
  /** 执行端停下来的原话（默认收起） */
  why: string;
  cats: ManifestFileReq[];
  /** 选了「从哪几份文件读」（继续时记为字段来源） */
  picked: string[]; onPick: (ids: string[]) => void; onChanged: () => void;
  /** 「补新文件」「其它指引」两个标签页的内容（由外面的弹框提供） */
  uploadPane: ReactNode; notePane: ReactNode;
}) {
  const [key, setKey] = useState<string | null>(null);
  /** 下拉 / 单选字段的页面选项：有就直接点选，不用打字 */
  const [options, setOptions] = useState<string[]>([]);
  const [chat, setChat] = useState<FieldChatMessage[]>([]);
  const [msg, setMsg] = useState("");
  const [direct, setDirect] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<MfTab>(() => (task.fieldChats && Object.keys(task.fieldChats).length ? "ai" : "direct"));
  // 先确认字段 key（新字段登记进字段表）
  useEffect(() => {
    let off = false;
    setKey(null); setError(null);
    api.learningField(task.id).then((r) => {
      if (off) return;
      setKey(r.key); setChat(task.fieldChats?.[r.key] ?? []); setOptions(r.options ?? []);
      if (r.options?.length) setTab("direct");
    }, (e) => { if (!off) setError(errorText(e)); });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, field.label]);
  const fname = fieldName(field);
  const value = key ? task.extraction?.fields.find((f) => f.key === key)?.value ?? (task.manifest as Record<string, unknown>)?.[key] : null;
  /** 下拉字段的现值不在页面选项里（如填了「旅游」）：执行端填不进去 */
  const badValue = options.length > 0 && value != null && value !== "" && !options.some((o) => o.toLowerCase() === String(value).trim().toLowerCase());
  const files = task.files.filter((f) => f.state === "synced");
  const catLabel = (k: string) => cats.find((c) => c.key === k)?.label ?? (k === "other" ? "未归类" : k);
  const lastAi = [...chat].reverse().find((m) => m.role === "ai");
  const ask = async (auto?: string) => {
    const text = auto ?? msg.trim();
    if (!key || !text) return;
    setBusy(true); setError(null);
    setChat((c) => [...c, { role: "user", text, at: new Date().toISOString() }]);
    if (!auto) setMsg("");
    try { setChat((await api.teachField(task.id, key, text, picked)).chat); }
    catch (e) { setError(errorText(e)); setChat((c) => c.slice(0, -1)); if (!auto) setMsg(text); }
    finally { setBusy(false); }
  };
  // 勾选文件后自动读：停 1 秒（方便连勾几份）就让助理从勾选的文件里找；同一组文件只自动读一次
  const autoDone = useRef("");
  useEffect(() => {
    if (!key || !picked.length || busy) return;
    const sig = [...picked].sort().join(",");
    if (sig === autoDone.current) return;
    const t = setTimeout(() => {
      autoDone.current = sig;
      const names = files.filter((f) => picked.includes(f.id)).map((f) => f.name.split("／").pop()).join("、");
      void ask(`从这几份文件里找「${field.label}」：${names}。需要换算的话说明原币金额和依据。`);
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, picked.join(","), busy]);
  // 对话有新消息时滚到底
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [chat.length, busy]);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const tabs: { key: MfTab; label: string }[] = [
    { key: "direct", label: "✍️ 直接填写" },
    { key: "ai", label: `🤖 让 AI 从文件里找${chat.length ? ` · ${chat.length}` : ""}` },
    { key: "upload", label: "📎 补新文件" },
    { key: "note", label: "💬 其它指引" },
  ];
  return (
    <div className="mf-help" data-testid="missing-field-helper">
      <div className="mf-head">
        <div className="mf-field"><span className="mf-tag">要填</span><b translate="no">{fname.name}{fname.zh ? `（${fname.zh}）` : ""}</b>{fname.role && <span className="mf-role" data-testid="mf-role">角色：{fname.role}</span>}</div>
        <div className="mf-line"><span className="mf-tag">问题</span><span>{field.need || `「${task.typeName}」${where ? `${where}的` : ""}页面要填${fname.role ? `${fname.role}的` : ""}「${fname.zh ?? fname.name}」，但任务资料里找不到，执行端停下来等你给。`}</span></div>
        <div className="mf-line"><span className="mf-tag">现在</span>{badValue
          ? <span className="err">「{String(value)}」不是页面上的选项，执行端填不进去 —— 请在「直接填写」里从页面选项中选一个</span>
          : value
          ? <span className="ok-text" data-testid="mf-value">✓ 已确定：<b translate="no">{String(value)}</b> —— 点右下角「按此继续」，执行端就按这个值填</span>
          : lastAi?.value
            ? <span>助理找到 <b translate="no">{lastAi.value}</b>，还没确认 —— 点下面的「用「{lastAi.value}」，记住方法」</span>
            : <span className="muted">还没有值 —— 用下面任一种方式补上</span>}</div>
        <details className="mf-why"><summary className="small muted">执行端的原话</summary><p className="pre small" translate="no">{why}</p></details>
      </div>

      <div className="tabs mf-tabs" role="tablist">
        {tabs.map((t) => <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>

      <div className="mf-pane" hidden={tab !== "direct"}>
        {options.length > 0 && <>
          <div className="small muted">页面上这一栏是下拉，选一个（以下是页面上的原文）：</div>
          <div className="mf-options" role="radiogroup" aria-label="页面选项">
            {options.map((o) => {
              const on = String(value ?? "").trim().toLowerCase() === o.toLowerCase();
              return <button key={o} type="button" role="radio" aria-checked={on} className={`mf-option${on ? " on" : ""}`} disabled={busy || !key} translate="no"
                onClick={() => void run(async () => { await api.setExtractedField(task.id, key!, o); })}>{on ? "✓ " : ""}{o}</button>;
            })}
          </div>
          {badValue && <p className="small err">现在的值「{String(value)}」不在页面选项里，执行端填不进去，请从上面选一个。</p>}
          <div className="small muted" style={{ marginTop: 4 }}>都不合适才自己填：</div>
        </>}
        {!options.length && <div className="small muted">知道值就直接填，例如职业填「Director」或「董事」。</div>}
        <div className="row" style={{ gap: 6 }}>
          <input aria-label="直接填写字段值" placeholder={field.label} value={direct} disabled={busy || !key} onChange={(e) => setDirect(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && direct.trim()) void run(async () => { await api.setExtractedField(task.id, key!, direct.trim()); setDirect(""); }); }} style={{ flex: 1 }} />
          <button className="btn btn-primary" disabled={busy || !key || !direct.trim()} onClick={() => void run(async () => { await api.setExtractedField(task.id, key!, direct.trim()); setDirect(""); })}>保存</button>
        </div>
      </div>

      <div className="mf-pane" hidden={tab !== "ai"}>
        <div className="small muted">勾选文件后助理自动去读；读不准或要换算，在下面告诉它，比如「在职证明月薪 10000 人民币，按当天汇率换算」。</div>
        <details className="mf-files-box" open={!chat.length}>
          <summary className="small">从哪几份文件读{picked.length ? `（已选 ${picked.length} 份）` : ""}</summary>
          <div className="mf-files">
            {files.map((f) => (
              <label key={f.id} className="check" title={f.name}>
                <input type="checkbox" checked={picked.includes(f.id)} disabled={busy} onChange={(e) => onPick(e.target.checked ? [...picked, f.id] : picked.filter((x) => x !== f.id))} />
                <span><span className="muted small">{catLabel(f.key)} · </span><span translate="no">{f.name.split("／").pop()}</span></span>
              </label>
            ))}
            {!files.length && <span className="small muted">这个任务还没有已传的文件</span>}
          </div>
        </details>
        <div className="fb-chat-list" ref={listRef}>
          {chat.map((m, i) => (
            <div key={i} className={`fb-msg fb-msg-${m.role}`}>
              <span className="small muted">{m.role === "user" ? "你" : "助理"}</span>
              <div className="pre" translate="no">{m.text}</div>
              {m.role === "ai" && m.value && <div className="small">找到：<b translate="no">{m.value}</b>{m.file ? `（${m.file.split("／").pop()}）` : ""}</div>}
            </div>
          ))}
          {busy && <div className="fb-msg fb-msg-ai small muted">助理正在看文件…（看图片可能要一两分钟）</div>}
        </div>
        <div className="row" style={{ gap: 6 }}>
          <input aria-label="告诉助理怎么找" placeholder="例如：看在职证明里的职位" value={msg} disabled={busy || !key} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void ask(); }} style={{ flex: 1 }} />
          <button className="btn" disabled={busy || !key || !msg.trim()} onClick={() => void ask()}>发送</button>
          {lastAi?.value && <button className="btn btn-primary" disabled={busy} onClick={() => void run(async () => { const t = await api.confirmTeach(task.id, key!); setChat(t.fieldChats?.[key!] ?? []); })}>用「{lastAi.value}」，记住方法</button>}
        </div>
      </div>

      <div className="mf-pane" hidden={tab !== "upload"}>{uploadPane}</div>
      <div className="mf-pane" hidden={tab !== "note"}>{notePane}</div>
      {error && <p className="small err">{error}</p>}
    </div>
  );
}
